<script setup lang="ts">
import type { Track, Artist, Album, Playlist, Library, Channel, Actor } from '~/types'
import type { PlayOptionsProps } from '~/composables/audio/usePlayOptions'

import { useQueue } from '~/composables/audio/queue'
import { useStore } from '~/store'
import { getMany, useDataStore } from '~/data'
import { generateTrackCreditString } from '~/utils/utils'
import usePlayOptions from '~/composables/audio/usePlayOptions'

import TrackFavoriteIcon from '~/components/favorites/TrackFavoriteIcon.vue'
import PlayButton from '~/components/audio/PlayButton.vue'

import Sync from '~/ui/Sync.vue'
import Gap from '~/ui/Gap.vue'

interface Props extends PlayOptionsProps {
  track: Track
  index: number

  showArt?: boolean
  isArtist?: boolean
  isAlbum?: boolean

  // TODO(wvffle): Remove after https://github.com/vuejs/core/pull/4512 is merged
  isPlayable?: boolean
  artist?: Artist | null
  album?: Album | null
  playlist?: Playlist | null
  library?: Library | null
  channel?: Channel | null
  account?: Actor | null
}

const props = withDefaults(defineProps<Props>(), {
  showArt: true,
  isArtist: false,
  isAlbum: false,

  artist: null,
  album: null,
  playlist: null,
  library: null,
  channel: null,
  account: null
})

const { currentTrack } = useQueue()
const { activateTrack } = usePlayOptions(props)

const store = useStore()
</script>

<template>
  <div
    class="layout-row"
    ui-gap="16"
    :class="[
      { active: currentTrack && track.id === currentTrack.id },
      $style.row,
    ]"
  >
    <div
      v-if="showArt"
      :class="$style['art-col']"
      @click.prevent.exact="activateTrack(track)"
    >
      <img
        v-if="track.cover"
        v-lazy="store.getters['instance/absoluteUrl'](track.cover.urls.small_square_crop)"
        :alt="track.title"
        :class="$style.image"
      >
      <img
        v-else-if="track.album?.cover?.urls.original"
        v-lazy="store.getters['instance/absoluteUrl'](track.album.cover.urls.small_square_crop)"
        :alt="track.title"
        :class="$style.image"
      >
      <img
        v-else
        :alt="track.title"
        :class="$style.image"
        src="../../../assets/audio/default-cover.png"
      >
    </div>
    <div
      tabindex="0"
      role="button"
      ui-shrinkable
      class="layout-stack"
      ui-gap="4"
      :class="$style['text-col']"
      @click="activateTrack(track) /* No! This is inaccessible HTML abuse! Just use <button>. */"
    >
      <span
        :class="{ 'color-accent' : track.id === currentTrack?.id }"
        ui-indicate-overflow
        ui-nowrap
      >
        {{ track.title }}
      </span>
      <div
        class="layout-row"
        ui-gap="4"
        ui-nowrap
      >
        <span ui-indicate-overflow>
          <Sync
            :use="() => getMany(useDataStore().get_artist, () => track.artist_credit.map(({ artist: { id } }) => id))"
            v-slot="{ value: { data: artists } }"
          >
            {{ generateTrackCreditString(track.artist_credit, artists ?? []) }}
          </Sync>
        </span>
        <Gap />
        <Icon icon="bi:clock" style="font-size: 0.7em;" />
        <human-duration
          v-if="track.uploads?.[0]?.duration"
          :duration="track.uploads[0]?.duration"
          :class="$style.duration"
        />
      </div>
    </div>
    <div
      class="layout-row"
      ui-gap="8"
    >
      <TrackFavoriteIcon
        v-if="store.state.auth.authenticated"
        tiny
        :track
        class="color-default-ghost"
      />
      <PlayButton
        dropdown-only
        :is-playable="track.is_playable"
        :track
        class="shape-square"
      />
    </div>
  </div>
</template>

<style module>
  .row {
    border-top: 1px solid var(--border-color);
    padding: 8px 0;
    height: 4.5em;
  }

  .row:first-child {
    border-top: none;
  }

  .art-col {
    cursor: pointer;
  }

  .image {
    width: var(--block-size-48);
    height: var(--block-size-48);
    border-radius: var(--fw-border-radius);
  }

  .text-col {
    cursor: pointer;
  }

  .duration {
    font-size: 0.85em;
    color: var(--discrete-text-color);
  }
</style>
